import classes from "./Overview.module.css";
import OverviewBox from "../../components/CreativeEditing/OverviewBox";
import { useNavigate, Link, useParams, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import MusicProjectModal from "../../components/CreativeEditing/MusicProjectModal";
import ServiceModal from "../../components/CreativeEditing/ServiceModal";
import SaveNotification from "../../components/UI/SaveNotification/SaveNotification";
import { useModalSearchParamHistory } from "../../hooks/useModalSearchParamHistory";
import memberImage from "../../public/fakeUser/noUser.webp";
import {
    formatCreativeExperience,
    formatCreativeRating,
    formatCreativeBookings,
    hasCreativeRating,
} from "../../util/creativeStats";

export const Overview = ({ creative }) => {
    const navigate = useNavigate();
    const { id: creativeId } = useParams();
    const location = useLocation();
    const [activeTab, setActiveTab] = useState('overview');
    const [isExpanded, setIsExpanded] = useState(false);
    const [numberOfJobsRendering, setNumberOfJobsRendering] = useState(3);
    const [selectedMusicProject, setSelectedMusicProject] = useState(null);
    const [selectedServiceProject, setSelectedServiceProject] = useState(null);

    const {
        isOpen: isServiceModalOpen,
        openOverlay: openServiceModal,
        closeOverlay: closeServiceModal,
    } = useModalSearchParamHistory({
        paramName: "service",
        isActiveInUrl: (url) => url.searchParams.has("service"),
        onInactive: () => setSelectedServiceProject(null),
        resetKey: creativeId,
    });

    const {
        isOpen: isMusicModalOpen,
        openOverlay: openMusicModal,
        closeOverlay: closeMusicModal,
    } = useModalSearchParamHistory({
        paramName: "music",
        isActiveInUrl: (url) => url.searchParams.has("music"),
        onInactive: () => setSelectedMusicProject(null),
        resetKey: creativeId,
    });
    const [overviewWidth, setOverviewWidth] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);

    // Phone navigation refs and state
    const namePhoneRef = useRef(null);
    const phoneNavRef = useRef(null);
    const mainWrapperRef = useRef(null);
    const stickSentinelRef = useRef(null);
    const unstickSentinelRef = useRef(null);
    const [isCompactNameVisible, setIsCompactNameVisible] = useState(false);
    const [isPhoneNavFixed, setIsPhoneNavFixed] = useState(false);
    const [passedStick, setPassedStick] = useState(false);
    const [hitUnstick, setHitUnstick] = useState(false);
    const [showSaveNotification, setShowSaveNotification] = useState(false);

    // Helper to get project count (prioritize items.length when available, fallback to projectCount)
    const getProjectCount = () => {
        if (creative?.projects?.items && Array.isArray(creative.projects.items)) {
            return creative.projects.items.length;
        }
        if (creative?.projectCount !== null && creative?.projectCount !== undefined) {
            return creative.projectCount;
        }
        return 0;
    };

    // Helper to get package count (prioritize items.length when available, fallback to packageCount)
    const getPackageCount = () => {
        if (creative?.packages?.items && Array.isArray(creative.packages.items)) {
            return creative.packages.items.length;
        }
        if (creative?.packageCount !== null && creative?.packageCount !== undefined) {
            return creative.packageCount;
        }
        return 0;
    };

    // Helper to get the display URL for the first project image (handles YouTube/Vimeo links and uploaded videos with thumbnails)
    const getProjectThumbnailUrl = (project) => {
        if (!project) return null;
        
        const firstImage = project.images?.[0];
        const firstProcessedImage = project.processedImages?.[0];
        
        // Check if first image is an object (ProjectImage) and whether it's a link or video file
        if (firstImage && typeof firstImage === 'object') {
            const urlToCheck = firstImage.url || '';
            const isLink = firstImage.type === 'link' || 
                (urlToCheck && typeof urlToCheck === 'string' && 
                 (urlToCheck.includes('youtube.com') || urlToCheck.includes('youtu.be') || urlToCheck.includes('vimeo.com')));

            const isVideoFile =
                urlToCheck && typeof urlToCheck === 'string' &&
                /\.(mp4|mov|avi|wmv|flv|webm|mkv|m4v)$/i.test(urlToCheck);
            
            // For YouTube/Vimeo links, use thumbnailUrl from original image (old behavior)
            if (isLink && firstImage.thumbnailUrl) {
                return firstImage.thumbnailUrl;
            }

            // For uploaded videos with a thumbnail, prefer processedThumbnailUrl from processedImages
            if (isVideoFile) {
                if (firstProcessedImage && typeof firstProcessedImage === 'object' && firstProcessedImage.processedThumbnailUrl) {
                    return firstProcessedImage.processedThumbnailUrl;
                }
                if (firstImage.thumbnailUrl) {
                    return firstImage.thumbnailUrl;
                }
            }
        }
        
        // For regular images, use processedUrl or fallback
        if (typeof firstProcessedImage === 'string') {
            return firstProcessedImage;
        }
        
        return firstProcessedImage?.processedUrl || firstProcessedImage?.url || firstImage?.url || null;
    };

    const projectCount = getProjectCount();
    const packageCount = getPackageCount();
    const hasProjects = projectCount > 0;
    const hasPackages = packageCount > 0;

        const handleResize = () => {
            if (window.innerWidth < 1400) {
                setNumberOfJobsRendering(2);
            } else {
                setNumberOfJobsRendering(3);
            }

        if (window.innerWidth <= 1138 && window.innerWidth >= 743) {
            const computed = Math.max(0, Math.floor(window.innerWidth - 398));
            setOverviewWidth(computed);
        } else if (window.innerWidth <= 743) {
            setOverviewWidth(window.innerWidth);
        } else {
            setOverviewWidth(null);
        }
    };

    useEffect(() => {
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Show save notification when navigating from edit pages
    useEffect(() => {
        if (location.state?.saved) {
            setShowSaveNotification(true);
            // Clear the state to prevent showing again on re-render
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, navigate, location.pathname]);

    // Phone navigation intersection observers
    useEffect(() => {
        const getPxVar = (name, fallback) => {
            const val = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
            const parsed = parseInt(val, 10);
            return Number.isFinite(parsed) ? parsed : fallback;
        };

        const headerHeight = getPxVar('--headerPhoneHeight', 53);
        const navHeight = getPxVar('--navPhoneHeight', 56);
        const lineHeight = getPxVar('--thickLineHeight', 4);

        const stickObserver = new IntersectionObserver(
            ([entry]) => {
                setPassedStick(!entry.isIntersecting);
            },
            {
                root: null,
                rootMargin: `-${headerHeight}px 0px 0px 0px`,
                threshold: 0,
            }
        );

        const unstickObserver = new IntersectionObserver(
            ([entry]) => {
                setHitUnstick(entry.isIntersecting);
            },
            {
                root: null,
                rootMargin: `-${headerHeight + navHeight + lineHeight}px 0px 0px 0px`,
                threshold: 0,
            }
        );

        if (stickSentinelRef.current) stickObserver.observe(stickSentinelRef.current);
        if (unstickSentinelRef.current) unstickObserver.observe(unstickSentinelRef.current);

        return () => {
            stickObserver.disconnect();
            unstickObserver.disconnect();
        };
    }, []);

    useEffect(() => {
        setIsPhoneNavFixed(passedStick && !hitUnstick);
    }, [passedStick, hitUnstick]);

    // Fix Safari pull-to-refresh viewport issue
    useEffect(() => {
        // Only apply fix on mobile Safari (below 742px)
        if (window.innerWidth > 742) return;

        const forceViewportRecalc = () => {
            // Force a layout recalculation by reading layout properties
            if (mainWrapperRef.current) {
                void mainWrapperRef.current.offsetHeight;
            }
            if (document.body) {
                void document.body.offsetHeight;
            }
            if (document.documentElement) {
                void document.documentElement.offsetHeight;
            }
            
            // Trigger a resize event to recalculate viewport
            window.dispatchEvent(new Event('resize'));
        };

        let scrollTimeout;
        let touchEndTimeout;
        let visibilityTimeout;

        // Listen for touch end events (when pull-to-refresh completes)
        const handleTouchEnd = () => {
            if (touchEndTimeout) clearTimeout(touchEndTimeout);
            
            // Wait for Safari's pull-to-refresh animation to complete
            touchEndTimeout = setTimeout(() => {
                forceViewportRecalc();
            }, 250);
        };

        // Handle scroll events when at top (after pull-to-refresh)
        const handleScroll = () => {
            if (window.pageYOffset === 0 || window.scrollY === 0) {
                if (scrollTimeout) clearTimeout(scrollTimeout);
                
                scrollTimeout = setTimeout(() => {
                    forceViewportRecalc();
                }, 150);
            }
        };

        // Listen for visibility change (when page becomes visible after pull-to-refresh)
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                if (visibilityTimeout) clearTimeout(visibilityTimeout);
                
                visibilityTimeout = setTimeout(() => {
                    forceViewportRecalc();
                }, 100);
            }
        };

        // Fix on initial load (in case page was refreshed)
        const handleLoad = () => {
            setTimeout(() => {
                forceViewportRecalc();
            }, 100);
        };

        // Fix when page becomes focused
        const handleFocus = () => {
            setTimeout(() => {
                forceViewportRecalc();
            }, 100);
        };

        window.addEventListener('touchend', handleTouchEnd, { passive: true });
        window.addEventListener('scroll', handleScroll, { passive: true });
        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('load', handleLoad);
        window.addEventListener('focus', handleFocus);

        // Also run immediately in case we're already loaded
        setTimeout(() => {
            forceViewportRecalc();
        }, 50);

        return () => {
            window.removeEventListener('touchend', handleTouchEnd);
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('load', handleLoad);
            window.removeEventListener('focus', handleFocus);
            if (scrollTimeout) clearTimeout(scrollTimeout);
            if (touchEndTimeout) clearTimeout(touchEndTimeout);
            if (visibilityTimeout) clearTimeout(visibilityTimeout);
        };
    }, []);

    // Scroll to top when tab changes
    useEffect(() => {
        if (mainWrapperRef.current) {
            const rect = mainWrapperRef.current.getBoundingClientRect();
            const absoluteTop = rect.top + window.pageYOffset - 80;
            window.scrollTo({ top: absoluteTop, behavior: 'auto' });
        } else {
            window.scrollTo({ top: 0, behavior: 'auto' });
        }
    }, [activeTab]);

    const toggleDropdown = () => {
        setShowDropdown(!showDropdown);
    };

    const handleBackButtonClick = () => {
        navigate(`/services-studios`);
    };

    const handleNextStepClick = () => {
        if (projectCount === 0) {
            navigate(`/creative-editing/${creative?.id}/project`);
        } else if (packageCount === 0) {
            navigate(`/creative-editing/${creative?.id}/package`);
        } else {
            navigate(`/creative-editing/${creative?.id}/profile`);
        }
    };

    const handleTabClick = (tab) => {
        if (tab === activeTab) return;

        setActiveTab(tab);

        // Update URL so browser back/forward navigates between tabs
        // Tabs map to:
        //   overview -> /creative-editing/:id/overview
        //   arbeiten -> /creative-editing/:id/arbeiten
        //   services -> /creative-editing/:id/services
        const safeId = creativeId || creative?.id;
        if (!safeId) return;

        navigate(`/creative-editing/${safeId}/${tab}`, { replace: false });
    };

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const handleVisualProjectClick = (project) => {
        navigate(`/creative-editing/${creative?.id}/project/${project.id}/overview`);
    };

    const handleServiceProjectClick = (service) => {
        setSelectedServiceProject(service);
        openServiceModal(service.id);
    };

    const handleCloseServiceModal = () => {
        closeServiceModal();
    };

    const handleMusicProjectClick = (project) => {
        setSelectedMusicProject(project);
        openMusicModal(project.id);
    };

    const handleCloseMusicModal = () => {
        closeMusicModal();
    };

    // Restore selected service/music from URL (browser back/forward, deep links)
    useEffect(() => {
        if (!isServiceModalOpen || !creative?.packages?.items) return;
        const serviceId = new URLSearchParams(location.search).get("service");
        if (!serviceId) return;
        const service = creative.packages.items.find((p) => p.id === serviceId);
        if (service) setSelectedServiceProject(service);
    }, [isServiceModalOpen, location.search, creative?.packages?.items]);

    useEffect(() => {
        if (!isMusicModalOpen || !creative?.projects?.items) return;
        const projectId = new URLSearchParams(location.search).get("music");
        if (!projectId) return;
        const project = creative.projects.items.find((p) => p.id === projectId);
        if (project) setSelectedMusicProject(project);
    }, [isMusicModalOpen, location.search, creative?.projects?.items]);

    // Keep tabs in sync with URL (so direct links & browser back work)
    useEffect(() => {
        const parts = location.pathname.split('/').filter(Boolean);
        const last = parts[parts.length - 1];

        if (last === 'arbeiten' || last === 'services' || last === 'overview') {
            setActiveTab(last);
        } else {
            // Any unknown / base path -> normalize UI to overview
            setActiveTab('overview');
        }
    }, [location.pathname]);

    // Get all packages (in real data, we don't have JobsInStudio/Pakete distinction like fake data)
    const allPackages = hasPackages ? creative?.packages?.items : [];
    
    // Get services array for overview
    const servicesArrayOverview = creative?.skills || [];

    // Render Overview Tab Content
    const renderOverviewTab = () => (
        <>
            <div className={classes.uberDichContainer}>
                <h2 className="titleSemibold3">Über mich</h2>
                <p onClick={toggleExpand} className={`regularLarge ${!isExpanded ? classes.clamp3 : ''}`}>
                    {creative?.description || ''}
                </p>
                {creative?.description?.length > 350 && (
                    <button className="semiboldMiddle" onClick={toggleExpand}>
                        {isExpanded ? 'Weniger anzeigen' : 'Weiterlesen'}
                    </button>
                )}

                <div className={classes.servicesContentOverview}>
                    {servicesArrayOverview?.slice(0, 3).map((service, index) => (
                        <div className={classes.serviceItemOverview} key={index}>
                            <span className="regularMiddle">{service}</span>
                        </div>
                    ))}
                    {servicesArrayOverview?.length > 3 && (
                        <div className={classes.allViewButtonOverviewContainer}>
                            <button onClick={toggleDropdown} className={`${classes.allViewButtonOverview} mediumLarge`}>+{servicesArrayOverview?.length - 3}</button>
                            {showDropdown && (
                                <div className={classes.dropdownContainerOverview}>
                                    {servicesArrayOverview?.slice(3).map((service, index) => (
                                        <div className={classes.serviceItemOverview} key={index}>
                                            <span className="regularMiddle">{service}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className={classes.horizontalLine}></div>
            <div className={classes.arbeitenContainer}>
                <div className={classes.arbeitenHeader}>
                    {hasProjects && (
                        <h2 className="titleSemibold3">Arbeiten</h2>
                    )}
                    {hasProjects && (
                        <button onClick={() => handleTabClick('arbeiten')}>
                            <span className="semboldXs">Alle anschauen</span> <span style={{color: "var(--grey-1)"}} className="regularXs">({projectCount})</span>
                        </button>
                    )}
                </div>
                {hasProjects ? (
                    <>
                        <div className={classes.arbeitenContent}>
                            {creative?.projects?.items?.slice(0, numberOfJobsRendering).map((project) => {
                                if (creative?.creativeType === 'VISUAL') {
                                    const thumbnailUrl = getProjectThumbnailUrl(project);
                                    return (
                                        <div className={classes.arbeitenItemContainer} key={project.id} onClick={() => handleVisualProjectClick(project)}>
                                            <div className={classes.arbeitenItem}>
                                                {thumbnailUrl && (
                                                    <img src={thumbnailUrl} alt={project.title} />
                                                )}
                                            </div>
                                            <div className={classes.arbeitenItemText}>
                                                <p style={{color: "var(--black)"}} className="regularXxs">{project.title}</p>
                                                <p style={{color: "#8A8A91"}} className="slimRegularMiniText">
                                                    {creative?.user?.firstName} {creative?.user?.lastName}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                } else {
                                    const audioData = project.audios?.[0];
                                    const coverImage = audioData?.processedCoverImage || audioData?.coverImage || project.images?.[0]?.url || project.processedImages?.[0]?.processedUrl;
                                    const album = audioData?.album || '';
                                    const durationMs = audioData?.durationMs;
                                    const duration = durationMs ? `${Math.floor(durationMs / 60000)}:${String(Math.floor((durationMs % 60000) / 1000)).padStart(2, '0')}` : '';
                                    return (
                                        <div className={classes.musicianItemContainer} key={project.id} onClick={() => handleMusicProjectClick(project)}>
                                            <div className={classes.musicianImage}>
                                                {coverImage && <img src={coverImage} alt={project.title} />}
                                            </div>
                                            <div className={classes.musicianTextContainer}>
                                                <div className={classes.musicianTitleContainer}>
                                                    <p className={`${classes.titleTruncate} titleSemibold3`}>{project.title}</p>
                                                    {album && <p style={{color: "var(--dark-grey-3)"}} className="regularMiddle">{album}</p>}
                                                </div>
                                                {duration && (
                                                    <div className={classes.musicianDurationContainer}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="8" height="9" viewBox="0 0 8 9" fill="none">
                                                            <path d="M7.63636 3.87119C8.12121 4.15066 8.12121 4.84934 7.63636 5.12881L1.09091 8.90164C0.60606 9.18111 0 8.83177 0 8.27283L0 0.727165C0 0.168226 0.606061 -0.18111 1.09091 0.0983596L7.63636 3.87119Z" fill="#1F1F1F"/>
                                                        </svg>
                                                        <p className="regularMiddle">{duration}</p>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                }
                            })}
                        </div>
                        <div className={classes.arbeitenContentTabletWrapper}>
                            <div className={classes.arbeitenContentTablet}>
                                {creative?.projects?.items?.slice(0, numberOfJobsRendering).map((project) => {
                                    if (creative?.creativeType === 'VISUAL') {
                                        const thumbnailUrl = getProjectThumbnailUrl(project);
                                        return (
                                            <div className={classes.arbeitenItemContainer} key={project.id} onClick={() => handleVisualProjectClick(project)}>
                                                <div className={classes.arbeitenItem}>
                                                    {thumbnailUrl && (
                                                        <img src={thumbnailUrl} alt={project.title} />
                                                    )}
                                                </div>
                                                <div className={classes.arbeitenItemText}>
                                                    <p style={{color: "var(--black)"}} className="regularXxs">{project.title}</p>
                                                    <p style={{color: "#8A8A91"}} className="slimRegularMiniText">
                                                        {creative?.user?.firstName} {creative?.user?.lastName}
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    } else {
                                        const audioData = project.audios?.[0];
                                        const coverImage = audioData?.processedCoverImage || audioData?.coverImage || project.images?.[0]?.url || project.processedImages?.[0]?.processedUrl;
                                        const album = audioData?.album || '';
                                        const durationMs = audioData?.durationMs;
                                        const duration = durationMs ? `${Math.floor(durationMs / 60000)}:${String(Math.floor((durationMs % 60000) / 1000)).padStart(2, '0')}` : '';
                                        return (
                                            <div className={classes.musicianItemContainer} key={project.id} onClick={() => handleMusicProjectClick(project)}>
                                                <div className={classes.musicianImage}>
                                                    {coverImage && <img src={coverImage} alt={project.title} />}
                                                </div>
                                                <div className={classes.musicianTextContainer}>
                                                    <div className={classes.musicianTitleContainer}>
                                                        <p className={`${classes.titleTruncate} titleSemibold3`}>{project.title}</p>
                                                        {album && <p style={{color: "var(--dark-grey-3)"}} className="regularMiddle">{album}</p>}
                                                    </div>
                                                    {duration && (
                                                        <div className={classes.musicianDurationContainer}>
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="9" viewBox="0 0 8 9" fill="none">
                                                                <path d="M7.63636 3.87119C8.12121 4.15066 8.12121 4.84934 7.63636 5.12881L1.09091 8.90164C0.60606 9.18111 0 8.83177 0 8.27283L0 0.727165C0 0.168226 0.606061 -0.18111 1.09091 0.0983596L7.63636 3.87119Z" fill="#1F1F1F"/>
                                                            </svg>
                                                            <p className="regularMiddle">{duration}</p>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }
                                })}
                            </div>
                        </div>
                    </>
                ) : (
                    <Link to={`/creative-editing/${creative?.id}/project`} className={classes.addJobsContainer}>
                        <h3 className="titleSemibold3">Arbeiten hinzufügen</h3>
                        <div className={classes.addJobsBox}>
                            <div className={classes.boxShadowOutside}>
                                <div className={classes.boxShadow}>
                                    <div className={classes.boxShadowIcon}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M16.0469 8.57858H0.046875V7.50226H16.0469V8.57858Z" fill="#1F1F1F"/>
                                            <path fillRule="evenodd" clipRule="evenodd" d="M8.58121 0.046875V16.0469H7.50489V0.046875H8.58121Z" fill="#1F1F1F"/>
                                        </svg>
                                    </div>
                                </div>
                                <span className="mediumLarge">Projekt erstellen</span>
                            </div>
                        </div>
                    </Link>
                )}
            </div>
            {hasProjects && (
                <div className={classes.horizontalLine}></div>
            )}
            {hasProjects && (
            <div className={classes.servicesContainer}>
                <div className={classes.arbeitenHeader}>
                    {hasPackages && (
                        <h2 className="titleSemibold3">Services</h2>
                    )}
                    {hasPackages && (
                        <button onClick={() => handleTabClick('services')} className="semboldXs">
                            Alle anschauen <span style={{color: "var(--grey-1)"}} className="regularXs">({packageCount})</span>
                        </button>
                    )}
                </div>
                {hasPackages ? (
                    <div className={classes.servicesColumns}>
                        <div className={classes.serviceColumn}>
                            <div className={classes.servicesContent}>
                                {allPackages.slice(0, 2).map((service) => (
                                        <div onClick={() => handleServiceProjectClick(service)} className={classes.serviceItem} key={`jobs-${service.id}`}>
                                            <div className={classes.serviceItemImage}>
                                                {service.processedPortfolioImages?.[0] && (
                                                    <img src={service.processedPortfolioImages[0]} alt={service.name} />
                                                )}
                                            </div>
                                            <div className={classes.serviceItemText}>
                                                <span className="semiboldMiddle">{service.name}</span>
                                                <span className="regularMiddle">{service.pricePerHour || service.price} € pro Stunde</span>
                                                <span style={{color: "var(--dark-grey-4)", marginBottom: "12px"}} className="regularXs">Du zahlst die Studiozeit des Fotografen (Nachbearbeitung im Preis inklusive)</span>
                                                <span className={`regularMiddle ${classes.serviceDescription}`}>{service.description}</span>
                                                {service.deliverables?.[0] && (
                                                    <div style={{marginTop: "4px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-start", gap: "8px"}}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="16" viewBox="0 0 22 16" fill="none">
                                                            <g clipPath="url(#clip0_15597_28902)">
                                                                <path d="M3.23047 7.4039L5.03474 5.5524C5.252 5.32568 5.54484 5.20288 5.83768 5.20288H8.60549C8.79442 5.20288 8.98334 5.25011 9.14393 5.34458L10.5609 6.15697C10.7309 6.25143 10.9104 6.29867 11.0993 6.29867H14.1222C14.7835 6.29867 15.3219 6.90324 15.3219 7.65895C15.3219 8.41467 14.7835 9.01924 14.1222 9.01924H9.17227" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                                                <path d="M15.416 7.36008L17.343 5.9998C17.5225 5.89589 17.6737 5.74475 17.7776 5.54637L18.6372 3.77989C18.7789 3.50594 19.015 3.30757 19.2984 3.23199L20.0447 3.02417C20.3092 2.9486 20.5832 2.99583 20.8193 3.15642C21.3578 3.50594 21.5089 4.30889 21.1405 4.87567L17.9759 9.8445C17.9098 9.93896 17.8342 10.024 17.7492 10.0995L15.7277 11.8094C15.5577 11.9605 15.3404 12.0361 15.1231 12.0455L7.48096 12.2911" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                                                <path d="M3.23088 7.37502L7.4062 11.9754C7.71794 12.3249 7.6896 12.8634 7.34953 13.1751L5.97979 14.4221L0.670898 8.56527L2.04063 7.31834C2.39015 7.00661 2.91915 7.0255 3.24033 7.37502H3.23088Z" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                                                <path d="M7.26416 3.37002V0.866721C7.26416 0.649453 7.53811 0.469971 7.87818 0.469971H15.936C16.2761 0.469971 16.55 0.649453 16.55 0.866721V3.37002" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                                            </g>
                                                            <defs>
                                                                <clipPath id="clip0_15597_28902">
                                                                    <rect width="21.8118" height="15.0954" fill="white"/>
                                                                </clipPath>
                                                            </defs>
                                                        </svg>
                                                        <span className="regularXss">{service.deliverables[0]}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                    </div>
                ) : (
                    <div className={classes.addJobsContainer}>
                        <h3 className="titleSemibold3">Paket Erstellen</h3>
                        <Link to={`/creative-editing/${creative?.id}/package`}  className={classes.addJobsBox}>
                            <div className={`${classes.boxShadow} ${classes.boxShadowPacket}`}>
                                <div className={classes.boxShadowText}>
                                    <span>Paket Erstellen</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M16.0469 8.57858H0.046875V7.50226H16.0469V8.57858Z" fill="#1F1F1F"/>
                                        <path fillRule="evenodd" clipRule="evenodd" d="M8.58121 0.046875V16.0469H7.50489V0.046875H8.58121Z" fill="#1F1F1F"/>
                                    </svg>
                                </div>
                            </div>
                        </Link>
                    </div>
                )}
            </div>
            )}
            {hasProjects && (
            <div className={classes.servicesContainerTablet}>
                <div className={classes.servicesHeaderTablet}>
                    {hasPackages && (
                        <h2 className="titleSemibold3">Services</h2>
                    )}
                    {hasPackages && (
                        <button onClick={() => handleTabClick('services')} className="semboldXs">
                            Alle anschauen <span style={{color: "var(--grey-1)"}} className="regularXs">({packageCount})</span>
                        </button>
                    )}
                </div>
                {hasPackages && (
                    <div className={classes.servicesContentTabletWrapper}>
                        <div className={classes.servicesContentTablet}> 
                            {allPackages.slice(0, 4).map((service) => (
                                <div onClick={() => handleServiceProjectClick(service)} className={classes.serviceItem} key={`service-${service.id}`}>
                                    <div className={classes.serviceItemImage}>
                                        {service.processedPortfolioImages?.[0] && (
                                            <img src={service.processedPortfolioImages[0]} alt={service.name} />
                                        )}
                                    </div>
                                    <div className={classes.serviceItemText}>
                                        <span className="semiboldMiddle">{service.name}</span>
                                        <span className="regularMiddle">{service.pricePerHour || service.price} € pro Stunde</span>
                                        <span 
                                            style={{color: "var(--dark-grey-4)"}} 
                                            className="slimRegularMiniText"
                                        >
                                            Du zahlst die Studiozeit des Fotografen (Nachbearbeitung im Preis inklusive)
                                        </span>
                                        {service.deliverables?.[0] && (
                                            <div style={{marginTop: "4px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-start", gap: "8px"}}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="16" viewBox="0 0 22 16" fill="none">
                                                    <g clipPath="url(#clip0_15597_28902)">
                                                    <path d="M3.23047 7.4039L5.03474 5.5524C5.252 5.32568 5.54484 5.20288 5.83768 5.20288H8.60549C8.79442 5.20288 8.98334 5.25011 9.14393 5.34458L10.5609 6.15697C10.7309 6.25143 10.9104 6.29867 11.0993 6.29867H14.1222C14.7835 6.29867 15.3219 6.90324 15.3219 7.65895C15.3219 8.41467 14.7835 9.01924 14.1222 9.01924H9.17227" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                                    <path d="M15.416 7.36008L17.343 5.9998C17.5225 5.89589 17.6737 5.74475 17.7776 5.54637L18.6372 3.77989C18.7789 3.50594 19.015 3.30757 19.2984 3.23199L20.0447 3.02417C20.3092 2.9486 20.5832 2.99583 20.8193 3.15642C21.3578 3.50594 21.5089 4.30889 21.1405 4.87567L17.9759 9.8445C17.9098 9.93896 17.8342 10.024 17.7492 10.0995L15.7277 11.8094C15.5577 11.9605 15.3404 12.0361 15.1231 12.0455L7.48096 12.2911" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                                    <path d="M3.23088 7.37502L7.4062 11.9754C7.71794 12.3249 7.6896 12.8634 7.34953 13.1751L5.97979 14.4221L0.670898 8.56527L2.04063 7.31834C2.39015 7.00661 2.91915 7.0255 3.24033 7.37502H3.23088Z" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                                    <path d="M7.26416 3.37002V0.866721C7.26416 0.649453 7.53811 0.469971 7.87818 0.469971H15.936C16.2761 0.469971 16.55 0.649453 16.55 0.866721V3.37002" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                                    </g>
                                                    <defs>
                                                    <clipPath id="clip0_15597_28902">
                                                    <rect width="21.8118" height="15.0954" fill="white"/>
                                                    </clipPath>
                                                    </defs>
                                                </svg>
                                                <span className="regularXs">{service.deliverables[0]}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            )}
        </>
    );

    // Render Arbeiten Tab Content
    const renderArbeitenTab = () => (
        <div className={classes.jobsContainer}>
            <h2 className="titleSemibold3">Arbeiten</h2>
            {creative?.creativeType === 'VISUAL' ? (
                <div className={classes.arbeitenContentTabWrapper}>
                    <div className={classes.arbeitenContenTab}>
                        {creative?.projects?.items?.map((project) => {
                            const thumbnailUrl = getProjectThumbnailUrl(project);
                            return (
                            <div className={classes.arbeitenItemContainer} key={project.id} onClick={() => handleVisualProjectClick(project)}>
                                <div className={classes.arbeitenItem}>
                                    {thumbnailUrl && (
                                        <img src={thumbnailUrl} alt={project.title} />
                                    )}
                                </div>
                                <div className={classes.arbeitenItemText}>
                                    <p className="regularXs">{project.title}</p>
                                    <p className="slimRegularMiniText" style={{color: "#8A8A91"}}>
                                        {creative?.user?.firstName} {creative?.user?.lastName}
                                    </p>
                                </div>
                            </div>
                            );
                        })}
                        <Link to={`/creative-editing/${creative?.id}/project`} className={classes.addJobsBox}>
                            <div className={classes.boxShadowOutside}>
                                <div className={classes.boxShadow}>
                                    <div className={classes.boxShadowIcon}>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M16.0469 8.57858H0.046875V7.50226H16.0469V8.57858Z" fill="#1F1F1F"/>
                                            <path fillRule="evenodd" clipRule="evenodd" d="M8.58121 0.046875V16.0469H7.50489V0.046875H8.58121Z" fill="#1F1F1F"/>
                                        </svg>
                                    </div>
                                </div>
                                <span className="mediumLarge">Projekt erstellen</span>
                            </div>
                        </Link>
                    </div>
                </div>
            ) : (
                <div className={classes.musicianContent}>
                    {creative?.projects?.items?.map((project) => {
                        const audioData = project.audios?.[0];
                        const coverImage = audioData?.processedCoverImage || audioData?.coverImage || project.images?.[0]?.url || project.processedImages?.[0]?.processedUrl;
                        const album = audioData?.album || '';
                        const durationMs = audioData?.durationMs;
                        const duration = durationMs ? `${Math.floor(durationMs / 60000)}:${String(Math.floor((durationMs % 60000) / 1000)).padStart(2, '0')}` : '';
                        
                        return (
                            <div className={classes.musicianItemContainer} key={project.id} onClick={() => handleMusicProjectClick(project)}>
                                <div className={classes.musicianImage}>
                                    {coverImage && <img src={coverImage} alt={project.title} />}
                                </div>
                                <div className={classes.musicianTextContainer}>
                                    <div className={classes.musicianTitleContainer}>
                                        <p className="titleSemibold3">{project.title}</p>
                                        {album && <p style={{color: "var(--dark-grey-3)"}} className="regularMiddle">{album}</p>}
                                    </div>
                                    {duration && (
                                        <div className={classes.musicianDurationContainer}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="8" height="9" viewBox="0 0 8 9" fill="none">
                                                <path d="M7.63636 3.87119C8.12121 4.15066 8.12121 4.84934 7.63636 5.12881L1.09091 8.90164C0.60606 9.18111 0 8.83177 0 8.27283L0 0.727165C0 0.168226 0.606061 -0.18111 1.09091 0.0983596L7.63636 3.87119Z" fill="#1F1F1F"/>
                                            </svg>
                                            <p className="regularMiddle">{duration}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                    <Link to={`/creative-editing/${creative?.id}/project`} className={classes.addJobsContainer}>
                    <div className={classes.addJobsBox}>
                        <div className={classes.boxShadowOutside}>
                            <div className={classes.boxShadow}>
                                <div className={classes.boxShadowIcon}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M16.0469 8.57858H0.046875V7.50226H16.0469V8.57858Z" fill="#1F1F1F"/>
                                        <path fillRule="evenodd" clipRule="evenodd" d="M8.58121 0.046875V16.0469H7.50489V0.046875H8.58121Z" fill="#1F1F1F"/>
                                    </svg>
                                </div>
                            </div>
                            <span className="mediumLarge">Projekt erstellen</span>
                        </div>
                    </div>
                    </Link>
                </div>
            )}
        </div>
    );

    // Render Services Tab Content
    const renderServicesTab = () => (
        <div className={classes.arbeitenContainer}>
            <h2 className="titleSemibold3">Services</h2>
            <div className={classes.servicesColumns}>
                <div className={classes.serviceColumn}>
                    {/* <h3 className="titleSemibold3">Services</h3> */}
                    <div className={classes.servicesContent}>
                        {allPackages.map((service) => (
                            <div onClick={() => handleServiceProjectClick(service)} className={`${classes.serviceItem} ${classes.serviceItemProject}`} key={`service-${service.id}`}>
                                <div className={classes.serviceItemImage}>
                                    {service.processedPortfolioImages?.[0] && (
                                        <img src={service.processedPortfolioImages[0]} alt={service.name} />
                                    )}
                                </div>
                                <div className={classes.serviceItemText}>
                                    <span className="semiboldMiddle">{service.name}</span>
                                    <span className="regularMiddle">{service.pricePerHour || service.price} € pro Stunde</span>
                                    <span style={{color: "var(--dark-grey-4)", marginBottom: "12px"}} className="regularXs">Du zahlst die Studiozeit des Fotografen (Nachbearbeitung im Preis inklusive)</span>
                                    <span className={`regularMiddle ${classes.serviceDescription}`}>{service.description}</span>
                                    {service.deliverables?.[0] && (
                                        <div style={{marginTop: "4px", display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "flex-start", gap: "8px"}}>
                                            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="16" viewBox="0 0 22 16" fill="none">
                                                <g clipPath="url(#clip0_15597_28902)">
                                                    <path d="M3.23047 7.4039L5.03474 5.5524C5.252 5.32568 5.54484 5.20288 5.83768 5.20288H8.60549C8.79442 5.20288 8.98334 5.25011 9.14393 5.34458L10.5609 6.15697C10.7309 6.25143 10.9104 6.29867 11.0993 6.29867H14.1222C14.7835 6.29867 15.3219 6.90324 15.3219 7.65895C15.3219 8.41467 14.7835 9.01924 14.1222 9.01924H9.17227" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                                    <path d="M15.416 7.36008L17.343 5.9998C17.5225 5.89589 17.6737 5.74475 17.7776 5.54637L18.6372 3.77989C18.7789 3.50594 19.015 3.30757 19.2984 3.23199L20.0447 3.02417C20.3092 2.9486 20.5832 2.99583 20.8193 3.15642C21.3578 3.50594 21.5089 4.30889 21.1405 4.87567L17.9759 9.8445C17.9098 9.93896 17.8342 10.024 17.7492 10.0995L15.7277 11.8094C15.5577 11.9605 15.3404 12.0361 15.1231 12.0455L7.48096 12.2911" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                                    <path d="M3.23088 7.37502L7.4062 11.9754C7.71794 12.3249 7.6896 12.8634 7.34953 13.1751L5.97979 14.4221L0.670898 8.56527L2.04063 7.31834C2.39015 7.00661 2.91915 7.0255 3.24033 7.37502H3.23088Z" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                                    <path d="M7.26416 3.37002V0.866721C7.26416 0.649453 7.53811 0.469971 7.87818 0.469971H15.936C16.2761 0.469971 16.55 0.649453 16.55 0.866721V3.37002" stroke="#CB1541" strokeWidth="1.13357" strokeMiterlimit="10"/>
                                                </g>
                                                <defs>
                                                    <clipPath id="clip0_15597_28902">
                                                        <rect width="21.8118" height="15.0954" fill="white"/>
                                                    </clipPath>
                                                </defs>
                                            </svg>
                                            <span className="regularXss">{service.deliverables[0]}</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                        <Link to={`/creative-editing/${creative?.id}/package`} className={classes.addJobsBox}>
                            <div className={`${classes.boxShadow} ${classes.boxShadowPacket}`}>
                                <div className={classes.boxShadowText}>
                                    <span>Paket Erstellen</span>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M16.0469 8.57858H0.046875V7.50226H16.0469V8.57858Z" fill="#1F1F1F"/>
                                        <path fillRule="evenodd" clipRule="evenodd" d="M8.58121 0.046875V16.0469H7.50489V0.046875H8.58121Z" fill="#1F1F1F"/>
                                    </svg>
                                </div>
                            </div>
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className={classes.overviewContainer}> 
            <header className={classes.headerPhone}>
                <div className={classes.headerTopContainer}> 
                    <button onClick={handleBackButtonClick} className={classes.backButtonPhone}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="9" height="17" viewBox="0 0 9 17" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd" d="M0 7.90197L7.90197 0L8.95756 1.05559L2.07028 7.94287L9 15.4501L7.90306 16.4626L0.0119278 7.9139L0 7.90197Z" fill="black"/>
                        </svg>
                        <span className="mediumLarge">{isCompactNameVisible ? creative?.displayName || '' : ''}</span>
                    </button>
                    <Link to={`/creative-editing/${creative?.id}/profile`} className={`${classes.editButtonPhone} mediumLarge`}>Bearbeiten</Link>
                </div>
            </header>
            <div className={classes.profilePhoneContainer}>
                <div className={classes.profileContainer}>
                    <div className={classes.memberImageWrapper}>
                        <div className={classes.memberImageContainer}>
                            <img src={creative?.processedProfilePicture || memberImage} alt="creative" />
                        </div>
                        <div className={classes.checkBoxContainer}>
                            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                                <path d="M12.3326 0C5.52145 0 0 5.52145 0 12.3326C0 19.1438 5.52145 24.6653 12.3326 24.6653C19.1438 24.6653 24.6653 19.1438 24.6653 12.3326C24.6653 5.52145 19.1438 0 12.3326 0ZM18.6857 10.0801C17.7491 11.1835 16.7874 12.2655 15.835 13.3558C14.9121 14.4131 13.9881 15.4698 13.063 16.5255C12.9423 16.6635 12.8196 16.7994 12.69 16.929C12.0907 17.5272 11.1909 17.609 10.5219 17.0838C9.12249 15.9851 7.72888 14.8785 6.35416 13.7493C5.45534 13.0111 5.4375 11.8819 6.27021 11.141C6.56561 10.8776 6.91717 10.7748 7.32224 10.7858C7.7782 10.7664 8.17331 10.9521 8.53063 11.2381C9.31401 11.8646 10.0969 12.4921 10.8808 13.1171C11.2103 13.3799 11.4113 13.359 11.6862 13.0447C13.0929 11.437 14.4986 9.82875 15.908 8.22315C16.0969 8.00698 16.2826 7.78345 16.5339 7.63339C17.2407 7.21152 18.0954 7.36159 18.6627 8.00593C19.1622 8.57261 19.1931 9.48192 18.6857 10.0801Z" fill="#DF315A"/>
                            </svg>
                        </div>
                    </div>
                    <div className={classes.stats}>
                        <div className={classes.stat}>
                            <span className={`${classes.statValue} semiboldMiddle`}>{formatCreativeExperience(creative)}</span>
                            <span className="regularXxs">Erfahrung</span>
                        </div>
                        <div className={classes.stat}>
                            <span className={`${classes.statValue} semiboldMiddle`}>
                                {formatCreativeRating(creative)}
                                {hasCreativeRating(creative) && (
                                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="11" viewBox="0 0 12 11" fill="none">
                                        <g clipPath="url(#clip0_4470_12135)">
                                        <path d="M8.80204 8.12174L9.24303 10.7064L5.91724 8.96697L8.61217 7.02539L8.74079 7.7665L8.80204 8.12174Z" fill="#1D1E1B"/>
                                        <path d="M3.09436 7.76638L3.22299 7.02527L0.53418 4.42221L4.25196 3.88323L5.91792 0.539062L7.58387 3.88323L11.3017 4.42221L2.59213 10.7063L3.09436 7.76638Z" fill="#1D1E1B"/>
                                        </g>
                                        <defs>
                                        <clipPath id="clip0_4470_12135">
                                        <rect width="10.7675" height="10.1672" fill="white" transform="translate(0.53418 0.539062)"/>
                                        </clipPath>
                                        </defs>
                                    </svg>
                                )}
                            </span>
                            <span className="regularXxs">Sterne</span>
                        </div>
                        <div className={classes.stat}>
                            <span className={`${classes.statValue} semiboldMiddle`}>{formatCreativeBookings(creative)}</span>
                            <span className="regularXxs">Buchungen</span>
                        </div>
                    </div>
                </div>
                <div className={classes.memberNameContainerPhone} ref={namePhoneRef}>
                    <h1 className="semiboldMiddle">{creative?.displayName || ''}</h1>
                    <p className="regularXxs">{creative?.jobTitle || ''}</p>
                </div>
            </div>
            {/* Stick sentinel: sits right above the phone nav container */}
            <div ref={stickSentinelRef} style={{ position: 'absolute', inset: 'auto auto auto auto' }}></div>
            <div ref={phoneNavRef} className={`${classes.phoneNavigationContainer} ${isPhoneNavFixed ? classes.phoneNavigationFixed : ''}`}>
                <nav className={classes.navigationPhone}>
                    <button className={`${classes.navButtonPhone} semiboldSmall ${activeTab === 'overview' ? classes.activePhone : ''}`} onClick={() => handleTabClick('overview')}>
                        Übersicht
                    </button>
                    {hasProjects && (
                        <button className={`${classes.navButtonPhone} semiboldSmall ${activeTab === 'arbeiten' ? classes.activePhone : ''}`} onClick={() => handleTabClick('arbeiten')}>
                            Arbeiten
                        </button>
                    )}
                    {hasPackages && (
                        <button className={`${classes.navButtonPhone} semiboldSmall ${activeTab === 'services' ? classes.activePhone : ''}`} onClick={() => handleTabClick('services')}>
                            Services
                        </button>
                    )}
                </nav>
                <div className={classes.thickLine}></div>    
            </div>
            {isPhoneNavFixed && (
                <div className={classes.phoneNavigationSpacer}></div>
            )}
            {/* Unstick sentinel: placed at main content top */}
            <div ref={unstickSentinelRef} style={{ height: 0, width: 0 }}></div>
            <div ref={mainWrapperRef} className={classes.mainWrapper}>
                <div className={classes.overviewContainerInner}> 
                    <div className={classes.overview} style={overviewWidth ? { width: overviewWidth } : undefined}>
                <button className={classes.backButton} onClick={handleBackButtonClick}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="20" viewBox="0 0 11 20" fill="none">
                        <path fillRule="evenodd" clipRule="evenodd" d="M0 9.6255L8.99855 0.626953L10.2006 1.82903L2.35773 9.67193L10.2486 18.2204L8.99941 19.3734L0.0172915 9.64279L0 9.6255Z" fill="#1F1F1F"/>
                    </svg>
                    <p className="semiboldSmall">Zurück</p>
                </button>
                <div className={classes.overviewContent}>
                    <OverviewBox creative={creative} />
                </div>
            </div>
            <div className={classes.overviewRight}>
                <div className={classes.tabsContainer}>
                    <header className={classes.header}>
                        <nav className={classes.tabs}>
                            <button className={`semiboldSmall ${activeTab === 'overview' ? classes.activeState : ''}`} onClick={() => handleTabClick('overview')}>Übersicht</button>
                            {hasProjects && (
                                <button className={`semiboldSmall ${activeTab === 'arbeiten' ? classes.activeState : ''}`} onClick={() => handleTabClick('arbeiten')}>Arbeiten</button>
                            )}
                            {hasPackages && (
                                <button className={`semiboldSmall ${activeTab === 'services' ? classes.activeState : ''}`} onClick={() => handleTabClick('services')}>Services</button>
                            )}
                        </nav>
                        <Link to={`/creative-editing/${creative?.id}/profile`} className={classes.editButton}>
                            Bearbeiten
                        </Link>
                    </header>
                    <div className={classes.tabContentFade} key={activeTab}>
                        {activeTab === 'overview' && renderOverviewTab()}
                        {activeTab === 'arbeiten' && renderArbeitenTab()}
                        {activeTab === 'services' && renderServicesTab()}
                    </div>
                </div>
                {(projectCount === 0 || packageCount === 0) && (
                    <div className={classes.overviewProgress}>
                        <button className={classes.overviewProgressButton} onClick={handleNextStepClick}>
                            <div className={classes.overviewProgressButtonContent}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                    <path style={{
                                        fill: projectCount === 0 ? "#B1B6BB" : "#34373B"
                                    }} d="M6.239 1.58354C11.8393 -1.67105 18.9863 0.254761 22.1962 5.86028C22.5522 6.46523 22.3481 7.23137 21.7507 7.57858C21.1532 7.9258 20.3927 7.72022 20.048 7.11833C17.5233 2.70933 11.8974 1.19468 7.52075 3.73811C6.92329 4.08533 6.14118 3.8923 5.79652 3.29041C5.45186 2.68852 5.64153 1.93075 6.2318 1.58772"/>
                                    <path style={{
                                        fill: packageCount === 0 ? "#B1B6BB" : "#34373B"
                                    }} d="M24.0009 12.2332C23.9904 18.7465 18.7548 24.0121 12.331 24.0016C11.6329 24.0088 11.077 23.447 11.0781 22.7521C11.0792 22.0573 11.6369 21.4973 12.3267 21.4984C17.3793 21.5067 21.4996 17.361 21.5078 12.271C21.5089 11.5762 22.0666 10.9911 22.7564 10.9922C23.4461 10.9933 24.0021 11.5384 24.0009 12.2249" />
                                    <path d="M5.86306 22.2059C0.263887 18.9493 -1.65658 11.7518 1.55533 6.14747C1.89717 5.53427 2.65775 5.32897 3.25509 5.6764C3.85244 6.02382 4.05623 6.79004 3.71136 7.3918C1.18504 11.7999 2.69791 17.4649 7.07362 20.0099C7.67096 20.3573 7.89635 21.1361 7.55147 21.7379C7.2066 22.3396 6.4604 22.5533 5.87026 22.2101" fill="#34373B"/>
                                </svg>
                                <p className="regularSmall">Nächster Schritt: {projectCount === 0 ? "Projekt erstellen" : "Pakete anbieten"}</p>
                            </div>
                            <svg xmlns="http://www.w3.org/2000/svg" width="7" height="14" viewBox="0 0 7 14" fill="none">
                                <path d="M0.3986 13.3203L6.38281 6.72166M6.36703 7.46886L0.382812 0.320312" stroke="#0B0418"/>
                            </svg>
                        </button>
                    </div>
                )}
                </div>
            </div>
            </div>
            {creative?.creativeType === 'MUSIC' && selectedMusicProject && (
                <MusicProjectModal
                    open={isMusicModalOpen}
                    onClose={handleCloseMusicModal}
                    project={selectedMusicProject}
                    creative={creative}
                    projectsArray={creative?.projects?.items}
                />
            )}
            {isServiceModalOpen && selectedServiceProject && (
            <ServiceModal
                open={isServiceModalOpen}
                onClose={handleCloseServiceModal}
                service={selectedServiceProject}
                creative={creative}
                servicesArray={creative?.packages?.items}
            />
            )}
            <SaveNotification 
                show={showSaveNotification} 
                onHide={() => setShowSaveNotification(false)} 
            />
        </div>
    )
}

export default Overview;